import {
  Table,
  Th,
  Thead,
  Tr,
  Flex,
  Text,
  Tbody,
  Td,
  useToast,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import { getIn } from 'formik';
import lodash from 'lodash';
import React, { useState, useCallback } from 'react';

import { portfolioCovenants as commonPortfolioCovenants } from '@finley-technologies/covenant-calculations';
import { FormulaEngine, Util } from '@finley-technologies/finley-types';

import { EditPostgresQueryModal } from './EditPostgresQuery';
import { updatePostgresQueryShell } from './postgresQueryConfig/updateShell';
import {
  AddIconButton,
  DeleteIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Select } from 'packages/common-frontend/components/Select';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';

const { cloneDeep, map } = lodash;

const createPostgresQuery = (
  idx: number,
): CreditAgreement.Config.PortfolioCovenant.PrincipalBalancePointInTimeQuery => ({
  name: `PLACEHOLDER_${idx}`,
  variables: [],
  type: 'principalBalancePointInTime',
  parameterConfig: {
    dateFormula: {
      engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
      type: 'formula',
      formula: 'EOMONTH(triggerDate, -1)',
    },
  },
});

export const PostgresQueryView: React.FC<{
  postgresQuery: CreditAgreement.Config.PortfolioCovenant.PostgresQuery;
  wipConfig?: WipConfig;
  basePropertyKey: string;
  fullPropertyKey: string;
  triggerDate: Util.FullDateStr;
  idx: number;
}> = ({
  postgresQuery,
  wipConfig,
  basePropertyKey,
  idx,
  fullPropertyKey,
  triggerDate,
}) => {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const deleteQuery = (wc: WipConfig) => {
    // getIn always returns any, needs typecast to filter
    const fields = getIn(
      wc.formProps.values,
      basePropertyKey,
    ) as CreditAgreement.Config.PortfolioCovenant.PostgresQuery[];

    wc.formProps.setFieldValue(
      basePropertyKey,
      fields.filter((_, varIdx) => varIdx !== idx),
    );
  };

  const onSave = useCallback(
    (query: CreditAgreement.Config.PortfolioCovenant.PostgresQuery) => {
      if (wipConfig) {
        const errors = commonPortfolioCovenants.validatePostgresQuery({
          postgresQuery: {
            ...query,
            variables: [
              { name: 'triggerDate', value: triggerDate, parseNumber: true },
              ...query.variables,
            ],
          },
        });
        if (errors.length) {
          toast({
            title: JSON.stringify(errors),
            status: 'error',
            position: 'top',
            isClosable: true,
          });
        }
        wipConfig.formProps.setFieldValue(fullPropertyKey, query);
      }
      setIsOpen(false);
    },
    [wipConfig, fullPropertyKey, toast, triggerDate],
  );

  const selectOptions: {
    value: CreditAgreement.Config.PortfolioCovenant.PostgresQueryType;
    label: CreditAgreement.Config.PortfolioCovenant.PostgresQueryType;
  }[] = CreditAgreement.Config.PortfolioCovenant.PostgresQueryTypeIterable.map(
    (el) => ({ value: el, label: el }),
  );

  return (
    <Tr>
      <Td>{postgresQuery.name}</Td>
      <Td>
        {wipConfig?.isMutateAllowed(fullPropertyKey) ? (
          <Select
            value={postgresQuery.type}
            onChange={(val) => {
              if (val) {
                const query = cloneDeep(
                  getIn(wipConfig.formProps.values, fullPropertyKey),
                ) as CreditAgreement.Config.PortfolioCovenant.PostgresQuery;
                query.type = val;
                const updatedQuery = updatePostgresQueryShell({
                  query,
                  wipConfig,
                });
                wipConfig.formProps.setFieldValue(
                  fullPropertyKey,
                  updatedQuery,
                );
              }
            }}
            options={selectOptions}
            width="xl"
          />
        ) : (
          <Text>{postgresQuery.type}</Text>
        )}
      </Td>

      <Td>
        <Editor
          defaultLanguage="json"
          value={JSON.stringify(postgresQuery.parameterConfig, null, 4)}
          options={{ readOnly: true }}
          theme="vs-dark"
          height="15vh"
        />
      </Td>
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <Td>
          <EditPostgresQueryModal
            isOpen={isOpen}
            onCancel={() => setIsOpen(false)}
            onSave={onSave}
            postgresQuery={postgresQuery}
            wipConfig={wipConfig}
          />
          <EditIconButton
            ariaLabel="edit query"
            label="Edit Query"
            onClick={() => setIsOpen(true)}
          />
          <DeleteIconButton
            ariaLabel="delete query"
            label="Delete Query"
            onClick={() => {
              deleteQuery(wipConfig);
            }}
          />
        </Td>
      )}
    </Tr>
  );
};

export const PostgresQueriesView: React.FC<{
  postgresQueries: CreditAgreement.Config.PortfolioCovenant.PostgresQuery[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
  triggerDate: Util.FullDateStr;
}> = ({ wipConfig, basePropertyKey, postgresQueries, triggerDate }) => {
  const addQuery = (wc: WipConfig) => {
    const queries = cloneDeep(getIn(wc.formProps.values, basePropertyKey));

    const query = createPostgresQuery(queries.length + 1);

    queries.push(query);

    wc.formProps.setFieldValue(basePropertyKey, queries);
  };

  return (
    <Flex flexDirection="column" mb="5">
      <Flex alignItems="center" mb="3">
        <Text fontWeight="bold" fontSize="large">
          Postgres Queries
        </Text>

        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <AddIconButton
            ariaLabel="add query"
            label="Add Query"
            onClick={() => {
              addQuery(wipConfig);
            }}
          />
        )}
      </Flex>

      {postgresQueries.length === 0 && (
        <Text p="3" border="1px" borderColor="gray.700">
          There are currently no postgres queries.
        </Text>
      )}
      {postgresQueries.length > 0 && (
        <Table borderWidth="1px">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Type</Th>
              <Th> Params</Th>
              {wipConfig && <Th>Edit/Delete</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {map(postgresQueries, (postgresQuery, idx) => {
              return (
                <PostgresQueryView
                  key={`${basePropertyKey}-${idx}`}
                  postgresQuery={postgresQuery}
                  basePropertyKey={basePropertyKey}
                  fullPropertyKey={`${basePropertyKey}[${idx}]`}
                  triggerDate={triggerDate}
                  idx={idx}
                  wipConfig={wipConfig}
                />
              );
            })}
          </Tbody>
        </Table>
      )}
    </Flex>
  );
};
